import { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { DATE_PRESETS, presetRange, matchingPreset, rangeLabel, parseIso, isoDate } from "@/lib/dates";

/**
 * The one date-range control, for every list that can be narrowed to a period.
 *
 * The register had two bare `<input type="date">`s squeezed into a single cell of a
 * six-column grid, with labels the controls beside them did not carry, and no way to
 * say "this month" without working out which day the month ends on. A period is what
 * an auditor asks about — "last quarter", "this financial year" — so the presets come
 * first and the exact dates sit under them for when the question is more precise.
 *
 * `value` is `{ from, to }` as ISO strings, which is the shape the API's `date_from` /
 * `date_to` take and the shape the URL carries; nothing is translated on the way.
 */
export default function DateRangeFilter({
  value = { from: "", to: "" },
  onChange,
  placeholder = "Any date",
  className,
  testid = "date-range",
}) {
  const [open, setOpen] = useState(false);
  // Two months side by side need ~600px; on a phone the second one ran off the screen.
  const [wide, setWide] = useState(() => (typeof window === "undefined" ? true : window.matchMedia("(min-width: 640px)").matches));
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    const onChange = (e) => setWide(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const { from, to } = value;
  const set = (patch) => onChange({ from, to, ...patch });
  const isSet = !!(from || to);
  const preset = matchingPreset(value);
  const summary = !isSet ? placeholder : preset ? preset.label : rangeLabel(value);

  const selected = { from: parseIso(from) || undefined, to: parseIso(to) || undefined };
  const onPick = (range) => {
    onChange({ from: range?.from ? isoDate(range.from) : "", to: range?.to ? isoDate(range.to) : "" });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" data-testid={testid} aria-label={`Filter by date: ${summary}`}
                className={cn("w-full justify-between font-normal", !isSet && "text-muted-foreground", className)}>
          <span className="flex items-center gap-2 min-w-0">
            <CalendarDays className="w-4 h-4 shrink-0 opacity-60" aria-hidden="true" />
            <span className="truncate">{summary}</span>
          </span>
          <span className="flex items-center gap-1 shrink-0">
            {isSet && (
              // A span, as in MultiSelect: a button inside a button is hoisted out by the
              // browser and takes the trigger's click with it.
              <span role="button" tabIndex={0} aria-label="Clear the date filter"
                    data-testid={`${testid}-clear`}
                    onClick={(e) => { e.stopPropagation(); onChange({ from: "", to: "" }); }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); onChange({ from: "", to: "" }); }
                    }}
                    className="p-0.5 rounded text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <X className="w-3.5 h-3.5" />
              </span>
            )}
            <ChevronDown className="w-4 h-4 opacity-50" aria-hidden="true" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto max-w-[calc(100vw-1rem)] p-0 overflow-y-auto max-h-[85vh]" align="start" collisionPadding={8}>
        <div className="flex flex-col sm:flex-row">
          <div className="sm:w-44 border-b sm:border-b-0 sm:border-r border-border p-2 space-y-0.5">
            {DATE_PRESETS.map((p) => (
              <button key={p.key} type="button" data-testid={`${testid}-preset-${p.key}`}
                      onClick={() => { onChange(presetRange(p.key)); setOpen(false); }}
                      className={cn("w-full text-left text-sm px-2.5 py-1.5 rounded-md hover:bg-muted",
                                    preset?.key === p.key && "bg-primary/10 text-primary font-medium")}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="p-3 space-y-3">
            <Calendar mode="range" selected={selected} onSelect={onPick} numberOfMonths={wide ? 2 : 1}
                      defaultMonth={selected.from || new Date()} className="p-0" />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor={`${testid}-from`} className="text-xs text-muted-foreground">From</Label>
                <Input id={`${testid}-from`} type="date" value={from} max={to || undefined}
                       onChange={(e) => set({ from: e.target.value })} data-testid={`${testid}-from`} />
              </div>
              <div>
                <Label htmlFor={`${testid}-to`} className="text-xs text-muted-foreground">To</Label>
                <Input id={`${testid}-to`} type="date" value={to} min={from || undefined}
                       onChange={(e) => set({ to: e.target.value })} data-testid={`${testid}-to`} />
              </div>
            </div>
            <div className="flex justify-between items-center">
              <Button type="button" variant="ghost" size="sm" disabled={!isSet}
                      onClick={() => onChange({ from: "", to: "" })} data-testid={`${testid}-reset`}>
                Clear
              </Button>
              <Button type="button" size="sm" onClick={() => setOpen(false)}>Done</Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
